import { useMemo } from "react";
import {
  calculateHomeOwnersInsuranceEscrow,
  calculatePrePaidMortgageInterest,
  calculatePropertyTaxesEscrow,
  getSumFromAccordionItemsArray,
  getSumOfCredits,
} from "../summaryBreakdownUtils";
import dayjs from "dayjs";

export const useClosingCalculations = ({
  details,
  downPayment,
  insurancePaymentMonth,
  plannedClosingDate,
  hasWaivedEscrow,
  insurance,
  earnestMoney,
  credits,
  taxes,
  state,
  lookingTo,
}) => {
  const {
    term,
    id,
    rate,
    loanAmount,
    closingCosts,
    mortgageInsurance: pmi,
    armFixedTermYears,
    amortizationType,
    loanTerm,
    apr,
    monthlyPayment,
    propertyTax: taxSchedule,
    amortizationSchedule: { periods },
    quoteFees: {
      rateCost,
      appraisalFees: { amount: appraisalFee },
      otherFees: {
        condoQuestions: condoFees,
        attorneyReview,
        constructionCompletionCertificate,
        vaFundingFee,
      },
      titleFees: {
        CPL,
        LenderTitleIns,
        OwnerTitleIns,
        SettlementFee,
        endorsementFees,
        otherFees: otherTitleFees,
        recordingFeeObj: {
          deed: deedRecordingFee,
          mortgage: mortgageRecordingFee,
          transferTaxes,
        },
      },
    },
  } = details;

  const firstPeriod = periods[0];
  const { principal, interest } = firstPeriod;

  const discountPoints = useMemo(
    () => (rateCost > 0 ? rateCost : 0),
    [rateCost]
  );
  const lenderCredit = useMemo(() => (rateCost > 0 ? 0 : rateCost), [rateCost]);

  const sumOfEndorsementFees = useMemo(
    () => getSumFromAccordionItemsArray(endorsementFees),
    [endorsementFees]
  );

  const sumOfOtherTitleFees = useMemo(
    () => getSumFromAccordionItemsArray(otherTitleFees),
    [otherTitleFees]
  );

  const titleFees =
    CPL +
    LenderTitleIns +
    OwnerTitleIns +
    SettlementFee +
    sumOfEndorsementFees +
    sumOfOtherTitleFees;

  const lenderFees = useMemo(
    () => [
      {
        title: "Admin fees",
        amount: 0,
      },
      {
        title: "Underwriting fees",
        amount: 0,
      },
      {
        title: "Processing fees",
        amount: 0,
      },
      {
        title: "Hidden fees",
        amount: 0,
      },
      {
        title: "Discount Points",
        amount: discountPoints,
      },
    ],
    [discountPoints]
  );
  const sumOfLenderFees = useMemo(
    () => getSumFromAccordionItemsArray(lenderFees),
    [lenderFees]
  );

  const servicesBorrowerCannotShopFor = useMemo(
    () => [
      {
        title: "Appraisal Fee",
        amount: appraisalFee,
      },
      {
        title: "Condo Fees",
        amount: condoFees,
      },
      {
        title: "Attorney Review Fee",
        amount: attorneyReview,
      },
      {
        title: "Construction Certificate",
        amount: constructionCompletionCertificate,
      },
    ],
    [appraisalFee, condoFees, attorneyReview, constructionCompletionCertificate]
  );
  const sumOfCannotShopFor = useMemo(
    () => getSumFromAccordionItemsArray(servicesBorrowerCannotShopFor),
    [servicesBorrowerCannotShopFor]
  );

  const governmentFees = useMemo(
    () => [
      {
        title: "Deed Recording Fee",
        amount: deedRecordingFee,
      },
      {
        title: "Mortgage Recording Fee",
        amount: mortgageRecordingFee,
      },
      {
        title: "Transfer Taxes",
        amount: transferTaxes,
      },
      {
        title: "VA Funding Fee",
        amount: vaFundingFee,
      },
      {
        title: "FHA Funding Fee",
        amount: 0,
      },
      {
        title: "Government Fees Other",
        amount: 0,
      },
    ],
    [deedRecordingFee, mortgageRecordingFee, transferTaxes, vaFundingFee]
  );
  const sumOfGovFees = useMemo(
    () => getSumFromAccordionItemsArray(governmentFees),
    [governmentFees]
  );

  const primaryItems = useMemo(
    () => [
      {
        title: "Closing Costs",
        amount: closingCosts,
      },
      {
        title: "Down Payment",
        amount: downPayment,
      },
    ],
    [closingCosts, downPayment]
  );
  const sumOfPrimaryItems = useMemo(
    () => getSumFromAccordionItemsArray(primaryItems),
    [primaryItems]
  );

  const mortgageInterestPrePaid = useMemo(
    () =>
      calculatePrePaidMortgageInterest(rate, loanAmount, plannedClosingDate),
    [rate, loanAmount, plannedClosingDate]
  );

  const prePaids = useMemo(
    () => [
      {
        title: "Mortgage Interest",
        amount: mortgageInterestPrePaid,
      },
      {
        title: "Homeowners Insurance",
        amount:
          insurance && (lookingTo === "Purchase" || lookingTo === "Build")
            ? insurance
            : 0,
      },
    ],
    [mortgageInterestPrePaid, insurance, lookingTo]
  );
  const sumOfPrePaids = useMemo(
    () => getSumFromAccordionItemsArray(prePaids),
    [prePaids]
  );

  const homeOwnersInsuranceEscrow = useMemo(
    () =>
      calculateHomeOwnersInsuranceEscrow(
        hasWaivedEscrow,
        insurance,
        insurancePaymentMonth,
        plannedClosingDate,
        lookingTo
      ),
    [hasWaivedEscrow, insurance, insurancePaymentMonth, plannedClosingDate]
  );

  const propertyTaxesEscrow = useMemo(
    () =>
      calculatePropertyTaxesEscrow({
        hasWaivedEscrow,
        taxes,
        state,
        plannedClosingDate,
        taxSchedule,
        lookingTo,
      }),
    [hasWaivedEscrow, taxes, state, plannedClosingDate, taxSchedule]
  );

  const escrows = useMemo(
    () => [
      {
        title: "Property Taxes",
        amount: propertyTaxesEscrow,
      },
      {
        title: "Homeowners Insurance",
        amount: homeOwnersInsuranceEscrow,
      },
    ],
    [mortgageInterestPrePaid, insurance]
  );
  const sumOfEscrows = useMemo(
    () => getSumFromAccordionItemsArray(escrows),
    [escrows]
  );
  const sumOfCredits = useMemo(
    () => getSumOfCredits(earnestMoney, credits, lookingTo),
    [earnestMoney, credits, lookingTo]
  );

  const totalCashToClose = useMemo(
    () =>
      Math.round(
        Number(sumOfPrimaryItems) +
          Number(sumOfPrePaids) +
          Number(sumOfEscrows) -
          Number(sumOfCredits)
      ),
    [sumOfPrimaryItems, sumOfPrePaids, sumOfEscrows, sumOfCredits]
  );

  return {
    sumOfCredits,
    sumOfEscrows,
    escrows,
    sumOfPrePaids,
    prePaids,
    sumOfPrimaryItems,
    primaryItems,
    sumOfGovFees,
    governmentFees,
    sumOfCannotShopFor,
    servicesBorrowerCannotShopFor,
    sumOfLenderFees,
    lenderFees,
    titleFees,
    lenderCredit,
    principal,
    interest,
    pmi,
    armFixedTermYears,
    amortizationType,
    loanTerm,
    rate,
    apr,
    monthlyPayment,
    closingCosts,
    term,
    id,
    totalCashToClose,
  };
};
